// src/pages/AdminBankTransfers.jsx
import React, { useCallback, useMemo, useRef, useState } from "react";
import SpinnerOverlay from "../components/ui/SpinnerOverlay";
import { useLessons } from "../contexts/LessonsContext";
import MonthPicker from "../components/ui/Monthpicker";
import AdminBankTransfersHeader from "../components/ui/AdminBankTransfersHeader";

/* ===================== Helpers ===================== */
const pad2 = (n) => String(n).padStart(2, "0");

const currentMonthStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
};

const monthToStartEnd = (monthStr) => {
  if (!monthStr || !/^\d{4}-\d{2}$/.test(monthStr)) return { start: "", end: "" };
  const [y, m] = monthStr.split("-").map(Number);
  const last = new Date(y, m, 0).getDate();
  return {
    start: `${y}-${pad2(m)}-01`,
    end:   `${y}-${pad2(m)}-${pad2(last)}`,
  };
};

const money = (n) => {
  const v = Number(n || 0);
  return v.toLocaleString("he-IL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Backend now returns rows for both teachers and suppliers (role: 'teacher' | 'supplier').
const roleLabel = (role) => (role === "supplier" ? "ספק" : "מורה");

/* ===================== Icons ===================== */
const SearchIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const ExternalLinkIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
  </svg>
);

/* ===================== Small pieces ===================== */
const RoleBadge = ({ role }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border whitespace-nowrap ${
      role === "supplier"
        ? "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200"
        : "bg-indigo-50 text-indigo-700 border-indigo-200"
    }`}
  >
    {roleLabel(role)}
  </span>
);

/* ===================== Page ===================== */
const AdminBankTransfers = () => {
  const { getAdminBankTransfersPreview, generateBankTransfersPdfAndSendToBank } = useLessons();

  // Stable date reference — not recreated on every render
  const now = useRef(new Date()).current;

  const [month,      setMonth]      = useState(currentMonthStr());
  const [search,     setSearch]     = useState("");
  const [loading,    setLoading]    = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error,      setError]      = useState("");
  const [preview,    setPreview]    = useState(null);
  const [pdfUrl,     setPdfUrl]     = useState("");
  const [sentToBank, setSentToBank] = useState(false);

  const { start, end } = useMemo(() => monthToStartEnd(month), [month]);

  const missingCount = Number(preview?.totals?.missingBankCount || 0);
  const teachersCount = Number(preview?.totals?.teachersCount ?? preview?.totals?.teachers ?? 0);
  const suppliersCount = Number(preview?.totals?.suppliersCount || 0);

  const canGenerate = useMemo(
    () => !!preview?.ok && missingCount === 0 && !loading && !generating,
    [preview, missingCount, loading, generating]
  );

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setError("");
    setPdfUrl("");
    setSentToBank(false);
    try {
      const data = await getAdminBankTransfersPreview({ start, end, search });
      setPreview(data);
    } catch (e) {
      setPreview(null);
      setError(e?.message || "שגיאה בטעינת תצוגה מקדימה להעברות בנקאיות");
    } finally {
      setLoading(false);
    }
  }, [getAdminBankTransfersPreview, start, end, search]);

  const generatePdf = useCallback(async () => {
    setGenerating(true);
    setError("");
    setPdfUrl("");
    setSentToBank(false);
    try {
      const res = await generateBankTransfersPdfAndSendToBank({ start, end, search });
      if (res?.pdfUrl) {
        setPdfUrl(res.pdfUrl);
        setSentToBank(!!res.sentToBank);
        window.open(res.pdfUrl, "_blank", "noopener,noreferrer");
      }
    } catch (e) {
      setError(e?.message || "שגיאה בהפקת PDF ושליחה לבנק");
    } finally {
      setGenerating(false);
    }
  }, [generateBankTransfersPdfAndSendToBank, start, end, search]);

  return (
    <>
      <AdminBankTransfersHeader />

      {(loading || generating) && (
        <SpinnerOverlay variant="glow" text="אנא המתן..." dark={false} blur={true} />
      )}

      <div className="min-h-screen max-w-7xl mx-auto p-4 sm:p-6" dir="rtl">

        {/* ── Filters ── */}
        <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-6 md:p-8 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">

            <MonthPicker
              value={month}
              onChange={setMonth}
              maxDate={now}
              minYear={2023}
              locale="he"
            />

            {/* Search — grows to fill */}
            <div className="flex-1 min-w-0">
              <div className="relative">
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                  <SearchIcon />
                </span>
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); loadPreview(); }
                  }}
                  placeholder="חיפוש לפי שם, מייל או טלפון..."
                  className="w-full px-4 pr-10 py-2 rounded-lg border border-gray-200 bg-white outline-none transition-all focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                />
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={loadPreview}
                disabled={loading}
                className="flex-1 sm:flex-none sm:w-32 px-4 py-2 rounded-lg text-sm font-medium bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all whitespace-nowrap"
              >
                {loading ? "טוען..." : "טען"}
              </button>

              <button
                onClick={generatePdf}
                disabled={!canGenerate}
                className={`flex-1 sm:flex-none sm:w-32 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all whitespace-nowrap ${
                  canGenerate ? "bg-emerald-600 hover:bg-emerald-700" : "bg-emerald-300"
                }`}
              >
                {generating ? "מפיק..." : "הפק PDF"}
              </button>
            </div>
          </div>

          {/* Alerts */}
          {(error || pdfUrl || (preview?.ok && missingCount > 0)) && (
            <div className="mt-4 space-y-3">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-3 text-sm">
                  {error}
                </div>
              )}

              {pdfUrl && (
                <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl p-3 text-sm">
                  <div className="font-semibold">
                    {sentToBank ? "PDF הופק ונשלח לבנק בהצלחה" : "PDF הופק בהצלחה"}
                  </div>
                  <a
                    href={pdfUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="underline font-medium inline-flex items-center gap-1 mt-1"
                  >
                    פתח קובץ PDF
                    <ExternalLinkIcon />
                  </a>
                </div>
              )}

              {preview?.ok && missingCount > 0 && (
                <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-xl p-3 text-sm">
                  <span className="font-semibold">לא ניתן להפיק PDF</span> — חסרים {missingCount} פרטי בנק (בנק/סניף/חשבון).
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── KPI Cards ── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold">👥</span>
              </div>
              <div className="min-w-0">
                <p className="text-sm text-blue-700">סה״כ מוטבים</p>
                <p className="text-2xl font-bold text-blue-900">{teachersCount + suppliersCount}</p>
                <p className="text-[11px] text-blue-600 mt-0.5">
                  {teachersCount.toLocaleString("he-IL")} מורים · {suppliersCount.toLocaleString("he-IL")} ספקים
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl border border-green-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold">₪</span>
              </div>
              <div className="min-w-0">
                <p className="text-sm text-green-700">סה״כ העברות</p>
                <p className="text-2xl font-bold text-green-900">{money(preview?.totals?.netIncome || 0)} ₪</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-50 to-fuchsia-50 p-4 rounded-xl border border-purple-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold">🏦</span>
              </div>
              <div className="min-w-0">
                <p className="text-sm text-purple-700">פרטי בנק</p>
                <p className="text-2xl font-bold text-purple-900">
                  {preview?.ok ? (missingCount > 0 ? `חסרים ${missingCount}` : "הכל תקין") : "—"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Empty state ── */}
        {!preview?.ok && (
          <div className="text-center py-12 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-gray-500 text-2xl">🏦</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">אין נתונים להצגה</h3>
            <p className="text-gray-600 text-sm">בחר חודש ולחץ על "טען"</p>
          </div>
        )}

        {/* ── Table ── */}
        {preview?.ok && (
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">

            {/* Table header bar */}
            <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 border-b border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                <div className="text-sm font-semibold text-gray-900">פירוט העברות בנקאיות — מורים וספקים</div>
                <div className="text-xs text-gray-500">
                  {preview?.meta?.periodStart || start} — {preview?.meta?.periodEnd || end}
                </div>
              </div>
            </div>

            {/*
              Column visibility strategy:
                xs–sm  (< 768px)  : שם + תפקיד | חשבון | סכום נטו | סטטוס
                md     (≥ 768px)  : + ת״ז + בנק + סניף
              min-w keeps it scrollable rather than squished at any breakpoint.
            */}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[420px] md:min-w-[720px]">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">שם</th>
                    {/* ת״ז, בנק, סניף — secondary info, hidden on small screens */}
                    <th className="hidden md:table-cell px-4 py-3 text-right text-sm font-semibold text-gray-700">ת״ז</th>
                    <th className="hidden md:table-cell px-4 py-3 text-right text-sm font-semibold text-gray-700">בנק</th>
                    <th className="hidden md:table-cell px-4 py-3 text-right text-sm font-semibold text-gray-700">סניף</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">חשבון</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">סכום נטו</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">סטטוס</th>
                  </tr>
                </thead>

                <tbody>
                  {(preview?.items || []).map((t, idx) => {
                    const ok = !!t.hasBankDetails;
                    const isSupplier = t.role === "supplier";
                    const missingAgreement = isSupplier && t.hasSupplierAgreement === false;
                    const missingTaxApproval = isSupplier && t.hasSupplierTaxApproval === false;

                    return (
                      <tr key={`${t.role || "teacher"}-${t.teacherId || idx}`} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-semibold whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span>{t.teacherName || "—"}</span>
                            <RoleBadge role={t.role} />
                          </div>
                        </td>
                        <td className="hidden md:table-cell px-4 py-3 text-sm text-gray-600">
                          {t.idNumber || "—"}
                        </td>
                        <td className="hidden md:table-cell px-4 py-3 text-sm text-gray-600">
                          {t.bankNumber || "—"}
                        </td>
                        <td className="hidden md:table-cell px-4 py-3 text-sm text-gray-600">
                          {t.bankBranch || "—"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 font-mono whitespace-nowrap">
                          {t.bankAccount || "—"}
                        </td>
                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
                            ₪{money(t.netIncome || 0)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex flex-col gap-1">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border whitespace-nowrap w-fit ${
                              ok
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                : "bg-red-50 text-red-700 border-red-200"
                            }`}>
                              {ok ? "תקין" : "חסר"}
                            </span>
                            {(missingAgreement || missingTaxApproval) && (
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border whitespace-nowrap w-fit bg-amber-50 text-amber-800 border-amber-200"
                                title={
                                  [
                                    missingAgreement ? "אין הסכם עמלה פעיל לספק — הופעלה ברירת מחדל" : null,
                                    missingTaxApproval ? "אין אישור ניכוי מס במקור מאושר — הופעלה ברירת מחדל" : null,
                                  ].filter(Boolean).join(" · ")
                                }
                              >
                                ⚠ חסר הסכם/אישור
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  {!preview?.items?.length && (
                    <tr>
                      <td className="px-4 py-14 text-center text-gray-600" colSpan={7}>
                        לא נמצאו תוצאות
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Table footer — stacks on mobile */}
            {/*preview?.items?.length > 0 && (
              <div className="px-4 sm:px-6 py-3 bg-gray-50 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 text-sm">
                <div className="font-medium text-gray-700">
                  סה״כ <span className="font-bold text-gray-900">{preview.items.length}</span> מורים
                </div>
                <div className="font-semibold text-gray-900">
                  סה״כ העברות:{" "}
                  <span className="text-emerald-700 font-bold">{money(preview?.totals?.netIncome || 0)} ₪</span>
                </div>
              </div>
            )*/}
          </div>
        )}
      </div>
    </>
  );
};

export default AdminBankTransfers;